import { Injectable, NgZone, inject } from "@angular/core";
import { MediaDevices } from "@auvious/media-tools";
import {
  AuviousRtc as Auvious,
  ConferenceMode,
  IConference,
  IConferenceMetadata,
  IConferenceMetadataUpdatedEvent,
  IConferenceSession,
  IConferenceSessionEventHandlers,
  ICreateConferenceOptions,
  IEndpoint,
  INetworkQualityReportEvent,
  IPublishedStream,
  IPublishOptions,
  IStream,
  StreamTypes,
  ViewStreamMetadataProvider
} from "@auvious/rtc";
import { BehaviorSubject, Observable, Subject, Subscriber } from "rxjs";
import { anonymizeIStream, sessionConferenceId } from "./utils";
import { DeviceService } from "./device.service";

import {
  CLAIM_TERMS_ACCEPTED,
  CLAIM_TERMS_ACCEPTED_AT,
  ConferenceMetadataKeyEnum,
  ConversationOriginEnum,
  EndpointTypeEnum,
  StreamTrackKindEnum,
  TerminateReasonEnum,
  UserRoleEnum,
  VideoFacingModeEnum,
  WhisperModeEnum
} from "../core-ui.enums";
import {
  DEFAULT_CALL_HOLD_STATE,
  ICallHoldState,
  IConferenceStream,
  IEndpointMetadata,
  IEndpointMetadataChangedEvent,
  EndpointStateEnum,
  IHook,
  IStreamMetadata,
  IStreamMetadataChangedEvent,
  IStreamMuteChangeEvent,
  PublicParam,
  StreamStateEnum,
  IConferenceView
} from "../models";
import {
  DEFAULT_ABR_VIEWER,
  IAutomaticBitrateAdaptationViewerPublicParams
} from "../models/application-options/PublicOptions";
import {
  BaseMetadata,
  ConferenceMetadataFactory,
  IntegrationMetadata,
  PointerMetadata,
  RecorderMetadata,
  SnapshotMetadata
} from "../models/Metadata";
import { AppConfigService } from "./app.config.service";
import { GenericErrorHandler } from "./error-handlers.service";
import { AuviousRtcService } from "./rtc.service";
import {
  anonymizeIEndpoint,
  createSentryLogger,
  debug,
  debugError
} from "./utils";
import { LocalMediaService } from "./local.media.service";
import { NotificationService } from "./notification.service";
import { UserService } from "./user.service";
import {
  DeviceCaptureErrorAction,
  DeviceCaptureStopSuccessAction,
  DeviceCaptureSuccessAction,
  ScreenShareAvailabilityChangedAction,
  WindowEventService
} from "./window.event.service";
import { captureMessage, getCurrentScope } from "@sentry/angular";
import { ApiResource } from "@auvious/common";

const TRACK_KIND = "trackKind";
const sentryLog = createSentryLogger("conference.service");

// error thrown by @auvious/rtc (dist/reduxImpl/conference/sagas.js) when the
// conference saga is torn down by a registration collapse (killAll). It only
// occurs on registration teardown, so it marks the session as recoverable.
const RTC_TEARDOWN_ERROR = "Terminated by unhandled action";

// Render reconciliation: a remote stream can be receiving media while never
// making it onto the screen — the SFU delivers, the PeerConnection accepts and
// acknowledges RTP, but no tile is ever built for it, so the user sees and
// hears nothing and the only way out is restarting the app. It is silent: no
// exception is thrown, so nothing is reported. We poll for the contradiction
// "receiving but not rendered", report it, and self-heal.
const RENDER_RECONCILE_INTERVAL_MS = 5000;
// Consecutive polls the contradiction must hold before we act. A remote track
// can read as briefly unmuted before its tile is wired up, so a single sample
// is not enough — same reasoning as the SDK watchdog's stall threshold.
const RENDER_RECONCILE_STRIKES = 3;

@Injectable()
export class ConferenceService {
  private rtcService = inject(AuviousRtcService);
  private userService = inject(UserService);
  private errorHandler = inject(GenericErrorHandler);
  private config = inject(AppConfigService);
  private alertService = inject(NotificationService);
  private windowEventService = inject(WindowEventService);
  private local = inject(LocalMediaService);
  private zone = inject(NgZone);

  private _session: IConferenceSession;
  private streams: { [streamId: string]: IStream } = {};
  private participants: { [endpointId: string]: IEndpoint<IEndpointMetadata> } =
    {};
  private auviousRtc: Auvious;
  private endpoints: {
    [endpoint: string]: { [correlationId: string]: IConferenceStream };
  };
  private mutedStreams: {
    [streamId: string]: { [trackKind: string]: boolean };
  };
  private endpointState: { [endpointId: string]: EndpointStateEnum };
  private streamState: { [streamId: string]: StreamStateEnum };
  private renderReconcileTimer: ReturnType<typeof setInterval> = null;
  private unrenderedStrikes: { [streamId: string]: number } = {};
  private streamFacingMode = new WeakMap<IStream, VideoFacingModeEnum>();
  private callHoldState: ICallHoldState;
  private conferenceMetadata: Map<string, IConferenceMetadata> = new Map();
  private conferenceMetadataQueue: Map<string, BaseMetadata> = new Map();

  private _streamAdded = new Subject<IStream>();
  public streamAdded$ = this._streamAdded.asObservable();

  private _streamRemoved = new Subject<IStream>();
  public streamRemoved$ = this._streamRemoved.asObservable();

  private _endpointStateChanged = new Subject<{
    endpoint: string;
    state: EndpointStateEnum;
  }>();
  public endpointStateChanged$ = this._endpointStateChanged.asObservable();

  private _streamsNeedPlay = new Subject<boolean>();
  public streamsNeedToPlay$ = this._streamsNeedPlay.asObservable();

  private _playPausedStreams = new Subject();
  public playPausedStreams$ = this._playPausedStreams.asObservable();

  private _endpointState = new Subject<{
    [endpointId: string]: EndpointStateEnum;
  }>();
  public endpointState$ = this._endpointState.asObservable();

  private _streamState = new Subject<{
    [streamId: string]: StreamStateEnum;
  }>();
  public streamState$ = this._streamState.asObservable();

  private _streamStateChanged = new Subject<{
    streamId: string;
    state: StreamStateEnum;
  }>();
  public streamStateChanged$ = this._streamStateChanged.asObservable();

  private _callHoldState = new Subject<ICallHoldState>();
  public callHoldStateChange$ = this._callHoldState.asObservable();

  private _networkQuality = new Subject<INetworkQualityReportEvent>();
  public networkQualityReport$ = this._networkQuality.asObservable();

  private _streamMuteChange = new Subject<IStreamMuteChangeEvent>();
  public streamMutedChange$ = this._streamMuteChange.asObservable();

  private _endpointJoined = new Subject<IEndpoint<IEndpointMetadata>>();
  public endpointJoined$ = this._endpointJoined.asObservable();

  private _localEndpointRecovered = new Subject<void>();
  public localEndpointRecovered$ = this._localEndpointRecovered.asObservable();

  private _endpointLeft = new Subject<IEndpoint<IEndpointMetadata>>();
  public endpointLeft$ = this._endpointLeft.asObservable();

  private _participantLeft = new Subject<IEndpoint<IEndpointMetadata>>();
  public participantLeft$ = this._participantLeft.asObservable();

  private _participantsChanged = new BehaviorSubject<{
    [endpointId: string]: IEndpoint<IEndpointMetadata>;
  }>({});
  public participantsChanged$ = this._participantsChanged.asObservable();

  private _facingModeChanged = new Subject<{
    streamId: string;
    facingMode: VideoFacingModeEnum;
  }>();
  public facingModeChanged$ = this._facingModeChanged.asObservable();

  private _localStreamWillPublish = new BehaviorSubject<IPublishOptions>(null);
  public localStreamWillPublish$ = this._localStreamWillPublish.asObservable();

  private _localStreamPublished = new BehaviorSubject<IPublishedStream>(null);
  public localStreamPublished$ = this._localStreamPublished.asObservable();

  private _streamMetadataChanged = new Subject<IStreamMetadataChangedEvent>();
  public streamMetadataChanged$ = this._streamMetadataChanged.asObservable();

  private _endpointMetadataChanged =
    new BehaviorSubject<IEndpointMetadataChangedEvent>(null);
  public endpointMetadataChanged$ =
    this._endpointMetadataChanged.asObservable();

  private _conferenceMetadataSet = new Subject<IConferenceMetadata>();
  public conferenceMetadataSet$ = this._conferenceMetadataSet.asObservable();

  private _conferenceMetadataRemoved = new Subject<IConferenceMetadata>();
  public conferenceMetadataRemoved$ =
    this._conferenceMetadataRemoved.asObservable();

  private _terminateConference = new Subject<TerminateReasonEnum>();
  public terminateConference$ = this._terminateConference.asObservable();

  private _confirmTerminateConference = new Subject<void>();
  public confirmTerminateConference$ =
    this._confirmTerminateConference.asObservable();

  private streamElements: Record<string, HTMLAudioElement | HTMLVideoElement> =
    {};

  public screenShareStream: IPublishedStream;

  private streamWarningsTimeouts: { [id: string]: any };
  private endedObserver: Subscriber<TerminateReasonEnum>;

  private preTerminateHooks: IHook[] = [];

  private isPublishingStream = false;

  private lastJoinArgs: {
    roomName: string;
    origin: ConversationOriginEnum;
    whisperMode?: WhisperModeEnum;
  } = null;
  private rejoinPending = false;

  private _activeConference: IConference;

  private _conferenceResource: ApiResource;

  constructor() {
    const rtcService = this.rtcService;

    this.auviousRtc = rtcService.client;
    this.reset();

    rtcService.common$.subscribe((common) => {
      this._conferenceResource = common.apiResourceFactory(
        "rtc-api/conferences"
      );
    });

    rtcService.connectionRegistered$.subscribe(() => {
      if (this.rejoinPending) {
        // defer so that the ConferenceStore (constructed after us) has already
        // processed the new endpoint id before the rejoin emits events
        queueMicrotask(() => this.rejoin());
      }
    });

    this.local.streamError$.subscribe((event) =>
      this.onLocalStreamError(event)
    );

    this.local.activeDeviceChanged$.subscribe((deviceKind) => {
      if (deviceKind === "videoinput") {
        this.setFacingModeForStream(
          this.localStream,
          this.local.mainStream.facingMode
        );
      }
    });

    this.local.localStreamReady$.subscribe((stream) => {
      if (
        stream.type === StreamTypes.SCREEN &&
        this.screenShareStream &&
        this.resolveScreenShareConflict(
          this.screenShareStream,
          stream as IPublishedStream
        ) === stream
      ) {
        // someone else streams a screen
        this.local.closeScreenStream();
      } else {
        // let it render first
        setTimeout(() => this.publish(stream), 0);
      }
    });

    // dummy stream replaced with the real deal
    this.local.localStreamReplaced$.subscribe((event) => {
      if (event.next) {
        this.streamElements[event.next.id] =
          this.streamElements[event.previous?.id];
      }

      delete this.streamElements[event.previous?.id];

      this.setFacingModeForStream(event.next, this.local.mainStream.facingMode);
      this.streamFacingMode.delete(event.previous);
    });

    this.local.streamMutedChange$.subscribe((event) => {
      if (event.stream) {
        this.mutedStreams[event.stream.id] = {
          ...this.mutedStreams[event.stream.id],
          [event.trackKind]: event.muted
        };
      }
    });

    this.local.streamRemoved$.subscribe((stream) => {
      this.unpublishStream(this.getStreamCorrelationId(stream));
      this.cleanStream(stream);
    });
  }

  private cleanStream(stream: IStream) {
    if (this.endpoints[stream.originator.endpoint]) {
      delete this.endpoints[stream.originator.endpoint]?.[
        this.getStreamCorrelationId(stream)
      ];
    }

    if (stream === this.screenShareStream) {
      if (this.isScreenShareStreamLocal) {
        this.windowEventService.sendMessage(
          new DeviceCaptureStopSuccessAction()
        );
      } /* TODO: if (isWidget || isPopup) */ else {
        // in case of remote screen share, disable local on widget and show card on audio call
        this.windowEventService.sendMessage(
          new ScreenShareAvailabilityChangedAction(false)
        );
      }

      this.screenShareStream = null;
    }

    if (stream.originator.endpoint !== this.myself.endpoint) {
      this.local.closeMediaStream(stream.mediaStream);
    }

    delete this.streams[stream.id];
    delete this.streamState[stream.id];
  }

  public get session(): IConferenceSession {
    return this._session;
  }

  public registerPreTerminateHook(hook: IHook) {
    this.preTerminateHooks.push(hook);
  }

  // we may have created a conference directly from rtc
  public setActiveConference(conference: IConference) {
    this._activeConference = conference;
  }

  public createConference(
    conferenceId?: string,
    interactionId?: string
  ): Promise<IConference> {
    return new Promise((resolve, reject) => {
      // do not call 'create' mutliple times in a flow
      if (
        conferenceId !== undefined &&
        this._activeConference?.name === conferenceId
      ) {
        return resolve(this._activeConference);
      }

      this.auviousRtc.on("conferenceCreated", (conference: IConference) => {
        debug("conferenceCreated", conference);
        sentryLog(["conferenceCreated", conference]);
        this._activeConference = conference;
        resolve(conference);
      });

      this.auviousRtc.on("createConferenceFailed", (error) => {
        try {
          const msg = error.data ? error.data.message : error.message;
          sentryLog(msg, "fatal");

          if (!!error.response && error.response.status === 404) {
            // don't log error on sentry
          } else if (!!this.endedObserver) {
            this.alertService.error("Could not create call.", { ttl: 3000 });
            this.errorHandler.handleError(error);
            this.endedObserver.error(error);
            this.endedObserver = null;
          }

          debug("createConferenceFailed", msg);
          reject(error);
        } catch (ex) {
          reject(ex);
          this.errorHandler.handleError(ex);
        }
      });

      const options: ICreateConferenceOptions = {
        mode: ConferenceMode.ROUTER,
        id: conferenceId,
        interactionId,
        metadata: {
          participant_limit: String(this.config.participantLimit)
        }
      };

      sentryLog(["Creating conference", options]);
      this.auviousRtc.createConference(options);
    });
  }

  public joinConference(
    roomName,
    origin: ConversationOriginEnum,
    whisperMode?: WhisperModeEnum
  ): Promise<Observable<any>> {
    this.lastJoinArgs = { roomName, origin, whisperMode };

    const endedObservable: Observable<any> = new Observable(
      (o) => (this.endedObserver = o)
    );

    this.local.freezeStreamType = true;

    return new Promise((resolve, reject) => {
      // if we are already joined, do not join again
      if (sessionConferenceId(this._session) === roomName) {
        return resolve(endedObservable);
      }

      // a session of another conference is stale (e.g. a join of the shared
      // client that failed before its own cleanup ran). Drop it and join.
      if (!!this._session) {
        sentryLog([
          "Dropping stale conference session",
          { stale: sessionConferenceId(this._session), roomName }
        ]);
        // leave() needs a joined conference; a failed join has none
        if (this._session.isEstablished()) {
          this._session.leave(TerminateReasonEnum.leave);
        }
        this.detachSession();
      }

      sentryLog(["Joining conference", { roomName, origin }]);

      /*
        Stream state order of events:
        - Any event may come first, except streamRecovered which comes after streamWarning
        - Only streamAdded and streamRecovered have new mediaStreams
        - if publishing, streamAdded comes right away, before streamPublished
        - streamFailed is followed by streamRemoved
        - streamRemoved is the last event
      */

      // the client has a single handler slot, so joins made elsewhere on the
      // shared client (appointment terminate, cobrowse) also land here, even
      // after this join is over. Take only this join's session.
      let sessionReceived = false;
      this.auviousRtc.on("newConferenceSession", (session) => {
        if (sessionReceived || sessionConferenceId(session) !== roomName) {
          sentryLog([
            "rtc.on.newConferenceSession: ignoring session of another join",
            { conferenceId: sessionConferenceId(session), roomName }
          ]);
          return;
        }
        sessionReceived = true;

        this._session = session;

        getCurrentScope().setTag("conferenceId", session.conferenceId);
        sentryLog(["rtc.on.newConferenceSession", session]);
        this.startRenderReconcile();
      });

      const eventHandlers: IConferenceSessionEventHandlers = {
        connecting: () => {
          sentryLog("rtc.on.connecting");
          debug(`connecting to conference ${roomName}`);
        },
        accepted: (session: IConferenceSession) => {
          this._endpointJoined.next({
            ...this.myself,
            metadata: { ...this.getMyMetadata(), whisperMode, origin }
          });

          debug("accepted", session);
          sentryLog(["rtc.on.accepted", session]);

          // If we are joining an already-on-hold conference (e.g. after a
          // transfer), reflect the hold state in the UI immediately so the
          // hold overlay shows before the local self-view renders. The RTC
          // holdUpdated event arrives later and would otherwise leave a
          // flicker where the agent sees themselves for a split second.
          if (this._session?.isOnHold()) {
            this.setCallHoldState({ loading: false, isOnHold: true });
          }

          this.republishLocalStreams();

          resolve(endedObservable);

          this.participants[this.myself.endpoint] = {
            ...this.myself,
            metadata: { ...this.getMyMetadata(), whisperMode, origin }
          };
          this._participantsChanged.next(this.participants);
        },
        endpointJoined: (originator: IEndpoint) => {
          try {
            debug("endpoint joined", originator);
            sentryLog([
              "rtc.on.endpointJoined",
              anonymizeIEndpoint(originator)
            ]);

            this.participants[originator.endpoint] = originator;
            this._participantsChanged.next(this.participants);

            this.endpointState[originator.endpoint] = EndpointStateEnum.Joined;

            // check if the endpoint comes from a refresh and has hanging screen share streams available
            if (
              this.screenShareStream &&
              this.screenShareStream.originator.username ===
                originator.username &&
              this.streamState[this.screenShareStream.id] ===
                StreamStateEnum.Warning
              // this.isEndpointSick(this.screenShareStream.id)
            ) {
              this.cleanStream(this.screenShareStream);
            }

            if (!this.endpoints[originator.endpoint]) {
              this.endpoints[originator.endpoint] = {};
            }

            this._endpointState.next(this.endpointState);
            this._endpointJoined.next(originator);
          } catch (ex) {
            this.errorHandler.handleError(ex);
          }
        },
        endpointGotSick: (originator: IEndpoint<IEndpointMetadata>) => {
          debug("endpoint got sick", originator);
          sentryLog(["rtc.on.endpointGotSick", anonymizeIEndpoint(originator)]);

          // ignore for now and handle only stream sick events
          this.endpointState[originator.endpoint] = EndpointStateEnum.Sick;
          this._endpointState.next(this.endpointState);
          this._endpointStateChanged.next({
            endpoint: originator.endpoint,
            state: EndpointStateEnum.Sick
          });
          debug("active endpoints", this.endpoints);
        },
        endpointRecovered: (originator: IEndpoint<IEndpointMetadata>) => {
          debug("endpoint recovered", originator);
          sentryLog([
            "rtc.on.endpointRecovered",
            anonymizeIEndpoint(originator)
          ]);

          // ignore for now and handle only stream sick events
          this.endpointState[originator.endpoint] = EndpointStateEnum.Joined;
          this._endpointState.next(this.endpointState);
          this._endpointStateChanged.next({
            endpoint: originator.endpoint,
            state: EndpointStateEnum.Joined
          });
          if (originator.endpoint === this.myself?.endpoint) {
            this._localEndpointRecovered.next();
          }
        },
        endpointLeft: (originator: IEndpoint<IEndpointMetadata>) => {
          const participant = this.participants[originator.endpoint];
          // originator.metadata.reason is available here (in case it was provided in session.leave())
          participant.metadata = {
            ...participant.metadata,
            ...originator.metadata
          };
          try {
            debug("endpoint left", originator);
            sentryLog(["rtc.on.endpointLeft", anonymizeIEndpoint(originator)]);

            if (this.endpoints[originator.endpoint]) {
              Object.values(this.endpoints[originator.endpoint]).forEach(
                (stream) => {
                  this.local.closeMediaStream(stream.mediaStream);
                }
              );

              delete this.endpoints[originator.endpoint];
            }

            delete this.participants[originator.endpoint];
            this._participantsChanged.next(this.participants);

            // mark as left
            this.endpointState[originator.endpoint] = EndpointStateEnum.Left;
            this._endpointState.next(this.endpointState);

            debug("removed participant", originator);
          } catch (ex) {
            this.errorHandler.handleError(ex);
          }

          this._endpointLeft.next(participant || originator);
        },
        publishStreamFailed: (data) => {
          debugError("publishStreamFailed event", data);
          sentryLog([
            "rtc.on.publishStreamFailed",
            { ...data, stream: anonymizeIStream(data.stream) }
          ]);

          this.errorHandler.handleError(data);
          this.alertService.error(
            `publish stream failed for stream ${data.stream.type}. Error ${data.error.message}`
          );
        },
        // fires only for local stream
        streamPublished: (stream: IPublishedStream) => {
          this.streams[stream.id] = stream;
          this.handleStreamAdded(stream);
          this.isPublishingStream = false;
          this._localStreamPublished.next(stream);

          sentryLog(["rtc.on.streamPublished", anonymizeIStream(stream)]);
        },
        // fires for local and remote before streamPublished
        streamAdded: (stream: IStream) => {
          try {
            this.streams[stream.id] = stream;
            sentryLog(["rtc.on.streamAdded", anonymizeIStream(stream)]);

            this.streamState[stream.id] = StreamStateEnum.Added;
            this._streamState.next(this.streamState);

            this.handleStreamAdded(stream);

            if (stream.originator.endpoint !== this.myself.endpoint) {
              this.setFacingModeForStream(stream, this.findFacingMode(stream));
            }

            // TODO: rtc should populate metadata
            stream.originator.metadata =
              this.participants[stream.originator.endpoint]?.metadata;

            this._streamAdded.next(stream);

            this._streamStateChanged.next({
              streamId: stream.id,
              state: StreamStateEnum.Added
            });
          } catch (ex) {
            this.errorHandler.handleError(ex);
          }
        },
        // could fire before streamAdded
        streamWarning: (stream: IStream) => {
          debug("stream warning", stream);
          sentryLog(["rtc.on.streamWarning", anonymizeIStream(stream)]);

          try {
            // in safari, stream warning comes just before endpoint left, showing 'connection lost'
            // for just a moment, before stream is removed. add a timeout to avoid this.
            if (!this.streamWarningsTimeouts) {
              this.streamWarningsTimeouts = {};
            }
            this.streamWarningsTimeouts[stream.id] = setTimeout(() => {
              this.streamState[stream.id] = StreamStateEnum.Warning;
              this._streamState.next(this.streamState);

              this._streamStateChanged.next({
                streamId: stream.id,
                state: StreamStateEnum.Warning
              });
            }, 200);
          } catch (ex) {
            this.errorHandler.handleError(ex);
          }
        },
        streamFailed: (stream: IStream) => {
          try {
            this.streamState[stream.id] = StreamStateEnum.Failed;
            this._streamState.next(this.streamState);

            this._streamStateChanged.next({
              streamId: stream.id,
              state: StreamStateEnum.Failed
            });
          } catch (ex) {
            this.errorHandler.handleError(ex);
          }
        },
        streamRecovered: (stream: IStream) => {
          debug("stream recovered", stream);
          sentryLog(["rtc.on.streamRecovered", anonymizeIStream(stream)]);

          try {
            // if we recover before the warning timeout, clear it.
            if (
              !!this.streamWarningsTimeouts &&
              stream.id in this.streamWarningsTimeouts
            ) {
              clearTimeout(this.streamWarningsTimeouts[stream.id]);
              delete this.streamWarningsTimeouts[stream.id];
              debug("cleared timeout", stream.id);
            }
            // Register the stream just like streamAdded does. streamRecovered
            // is the only success path that reaches the UI when a subscription's
            // original streamAdded was swallowed (unregistered/throwing handler
            // at first success flips the SDK's `published` flag anyway, so it
            // then only ever emits streamRecovered). Without this the stream
            // renders but is invisible to everything keyed on this.streams —
            // getStream(), refreshRemoteAudio(), mute lookups, and the render
            // reconcile above cannot see it to rescue or report it.
            this.streams[stream.id] = stream;
            this.streamState[stream.id] = StreamStateEnum.Added;
            this._streamState.next(this.streamState);
            this.handleStreamAdded(stream);

            this._streamAdded.next(stream);

            this._streamStateChanged.next({
              streamId: stream.id,
              state: StreamStateEnum.Added
            });
          } catch (ex) {
            this.errorHandler.handleError(ex);
          }
        },
        trackMuted: (stream: IStream) => {
          debug("remote stream muted", stream);
          sentryLog(["rtc.on.trackMuted", anonymizeIStream(stream)]);

          try {
            if (TRACK_KIND in stream) {
              this.mutedStreams[stream.id] = {
                ...this.mutedStreams[stream.id],
                [(stream as IStream)[TRACK_KIND]]: true
              };
            }

            if (stream.type !== StreamTypes.SCREEN) {
              this.handleStreamAdded(
                this.endpoints[stream.originator.endpoint]?.[
                  this.getStreamCorrelationId(stream)
                ]
              );

              this._streamMuteChange.next({
                stream,
                muted: true,
                trackKind: stream[TRACK_KIND]
              });
            }
          } catch (ex) {
            this.errorHandler.handleError(ex);
          }
        },
        trackUnmuted: (stream: IStream) => {
          debug("remote stream unmuted", stream);
          sentryLog(["rtc.on.trackUnmuted", anonymizeIStream(stream)]);

          try {
            if (!!this.mutedStreams[stream.id] && TRACK_KIND in stream) {
              delete this.mutedStreams[stream.id][
                (stream as IStream)[TRACK_KIND]
              ];
            }

            this.handleStreamAdded(
              this.endpoints[stream.originator.endpoint]?.[
                this.getStreamCorrelationId(stream)
              ]
            );

            if (stream.type !== StreamTypes.SCREEN) {
              this._streamMuteChange.next({
                stream,
                muted: false,
                trackKind: stream[TRACK_KIND]
              });
            }
          } catch (ex) {
            this.errorHandler.handleError(ex);
          }
        },
        streamRemoved: (stream: IStream) => {
          debug("stream removed", stream, this.endpoints);
          sentryLog(["rtc.on.streamRemoved", anonymizeIStream(stream)]);

          try {
            this._streamRemoved.next(stream);

            if (stream.type !== StreamTypes.SCREEN) {
              // in case a popup is closed and the customer has joined from both the widget and the popup,
              // we will not get an endpointLeft event immediately. So we rely on the streamRemoved to notify
              // the agent
              this._participantLeft.next(
                this.participants[stream.originator.endpoint]
              );

              if (
                this.endpoints[stream.originator.endpoint]?.[
                  this.getStreamCorrelationId(stream)
                ]
              ) {
                delete this.endpointState[stream.originator.endpoint];
              }
            }

            this.cleanStream(stream);
          } catch (ex) {
            this.errorHandler.handleError(ex);
          }
        },
        holdUpdated: (event: {
          enabled: boolean;
          userId: string;
          userEndpointId: string;
        }) => {
          debug("rtc.on.holdUpdated", event);
          sentryLog(["rtc.on.holdUpdated", event]);

          this.setCallHoldState({
            loading: false,
            isOnHold: event.enabled,
            username: event.userId,
            endpoint: event.userEndpointId
          });

          // temp fix for:
          // 1. call is on hold
          // 2. rtc mutes camera/mic track
          // 3. rtc camera track may be replaced with canvas track by bg filter
          // 4. unhold; canvas/mic track is unmuted
          // 5. but camera/mic track remains muted because state is not yet synced
          const cameraTrack: MediaStreamTrack =
            this.local.pipe.streamIn?.getVideoTracks()[0];
          if (
            !event.enabled &&
            this.localStream?.isMuted("video") === false &&
            cameraTrack?.enabled === false
          ) {
            cameraTrack.enabled = true;
          }
          const micTrack: MediaStreamTrack =
            this.local.pipe.streamIn?.getAudioTracks()[0];
          if (
            !event.enabled &&
            this.localStream?.isMuted("audio") === false &&
            micTrack?.enabled === false
          ) {
            micTrack.enabled = true;
          }
        },
        failed: (error) => {
          sentryLog(["rtc.on.failed", error], "error");
          getCurrentScope().setTag("conferenceId", null);

          if (
            error?.message === RTC_TEARDOWN_ERROR &&
            this.endedObserver &&
            this.lastJoinArgs
          ) {
            // the registration collapsed (network drop or re-registration)
            // and took the session down with it. The call itself may still be
            // alive server-side, so keep the room intact and arm a rejoin for
            // when the connection registers again.
            sentryLog([
              "rtc.on.failed: registration teardown, arming rejoin",
              error
            ]);
            this.detachSession();
            this.rejoinPending = true;
          } else if (
            error.response?.status === 404 ||
            (error.response?.status === 403 &&
              error.response?.data.error === "participant limit reached")
          ) {
            // don't log error on sentry
          } else if (this.endedObserver) {
            this.alertService.error("Could not join call.", { ttl: 3000 });
            this.errorHandler.handleError(error);
            this.endedObserver.error(error);
            this.endedObserver = null;
          } else {
            this.errorHandler.handleError(error);
          }

          // the session is set before the join request, so a failed join
          // would otherwise leave it behind and block the next join
          this._session = null;

          debug("conference failed", error.data?.message ?? error.message);
          reject(error);
        },
        ended: (payload: any) => {
          debug("conference session ended");
          sentryLog(["rtc.on.ended", payload]);
          getCurrentScope().setTag("conferenceId", null);

          this._session = null;
          this._activeConference = null;
          if (!!this.endedObserver) {
            this.endedObserver.next(TerminateReasonEnum.terminate);
            this.endedObserver = null;
          }
        },
        networkQualityReport: (report: INetworkQualityReportEvent) => {
          this._networkQuality.next(report);
        },
        streamMetadataUpdated: (event: IStreamMetadataChangedEvent) => {
          sentryLog(["rtc.on.streamMetadataUpdated", event]);
          this._streamMetadataChanged.next(event);
        },
        endpointMetadataUpdated: (event: IEndpointMetadataChangedEvent) => {
          sentryLog(["rtc.on.endpointMetadataUpdated", event]);
          this.participants[event.userEndpointId].metadata = {
            ...this.participants[event.userEndpointId].metadata,
            ...event.newMetadata
          };

          this._participantsChanged.next(this.participants);
          this._endpointMetadataChanged.next(event);
        },
        conferenceMetadataRemoved: (event: IConferenceMetadataUpdatedEvent) => {
          if (this.conferenceMetadata.has(event.key)) {
            sentryLog([
              "rtc.on.conferenceMetadataRemoved",
              { ...event, value: undefined }
            ]);
            const meta = this.conferenceMetadata.get(event.key);
            this.conferenceMetadata.delete(event.key);

            if (event.userId !== this.myself.username) {
              this.conferenceMetadataQueue.delete(event.key);
              this._conferenceMetadataRemoved.next(meta);
            }
          }
        },
        conferenceMetadataSet: (event: IConferenceMetadataUpdatedEvent) => {
          const meta = ConferenceMetadataFactory.fromEvent(event);
          if (meta) {
            sentryLog([
              "rtc.on.conferenceMetadataSet",
              { ...event, value: undefined }
            ]);
            this.conferenceMetadata.set(event.key, meta);
            this.tryNotifyForMetadata(meta);
          }
        }
      };

      try {
        const metadata: IEndpointMetadata = {
          ...this.getMyMetadata(),
          origin,
          whisperMode
        };

        const getAbraMetadata = (
          viewer: boolean,
          firstReducePublisherVideoBitrate: number = DEFAULT_ABR_VIEWER.firstReducePublisherVideoBitrate,
          minSecondsBeforeStoppingVideo: number = DEFAULT_ABR_VIEWER.minSecondsBeforeStoppingVideo,
          minSecondsBeforeStartingVideo: number = DEFAULT_ABR_VIEWER.minSecondsBeforeStartingVideo
        ) => ({
          "automatic-bandwidth-adaptation": viewer ? "enabled" : "disabled",
          "automatic-bandwidth-adaptation.first-reduce-publisher-video-bitrate": `${firstReducePublisherVideoBitrate}`,
          "automatic-bandwidth-adaptation.min-seconds-before-stopping-video": `${minSecondsBeforeStoppingVideo}`,
          "automatic-bandwidth-adaptation.min-seconds-before-starting-video": `${minSecondsBeforeStartingVideo}`
        });

        let viewStreamMetadataProvider: ViewStreamMetadataProvider;

        const callQuality = this.local.getCallQualityOptions();
        if (callQuality && callQuality.viewer) {
          viewStreamMetadataProvider = (stream) =>
            getAbraMetadata(
              callQuality.viewer.autoAdapt,
              callQuality.viewer.firstReducePublisherVideoBitrate,
              callQuality.viewer.minSecondsBeforeStoppingVideo,
              callQuality.viewer.minSecondsBeforeStartingVideo
            );
        } else {
          const abra: IAutomaticBitrateAdaptationViewerPublicParams =
            this.config.publicParam(
              PublicParam.AUTOMATIC_BITRATE_ADAPTATION_VIEWER
            );

          if (abra) {
            viewStreamMetadataProvider = (stream) =>
              getAbraMetadata(
                abra.viewer,
                abra.firstReducePublisherVideoBitrate,
                abra.minSecondsBeforeStoppingVideo,
                abra.minSecondsBeforeStartingVideo
              );
          }
        }

        this.zone.runOutsideAngular(() =>
          this.auviousRtc
            .joinConference({
              id: roomName,
              eventHandlers,
              statistics: this.config.statistics,
              metadata,
              viewStreamMetadataProvider
            })
            // avoid sentry logging, is handled in onfailed above
            .catch((ex) => debugError(ex))
        );
      } catch (ex) {
        this.errorHandler.handleError(ex);
        reject(ex);
      }
    });
  }

  /** forget the session without leaving it, and stop its events reaching us */
  private detachSession() {
    if (this._session) {
      this._session.eventHandlers = {};
      this._session = null;
    }
  }

  public getMyParticipantMetadata(): IEndpointMetadata {
    if (this.participants[this.myself.endpoint]) {
      return this.participants[this.myself.endpoint].metadata;
    }
  }

  private getMyMetadata(): IEndpointMetadata {
    const activeUser = this.userService.getActiveUser();
    const activeUserDetails = this.userService.getUserDetails();
    const roles = activeUser.getRoles();
    const displayName =
      activeUserDetails?.displayName || activeUserDetails?.name;
    const name = this.userService.isAgent
      ? this.config.agentDisplayName(displayName)
      : displayName;

    return {
      roles,
      capabilities: activeUser.getCapabilities(),
      language: this.config.language,
      type: EndpointTypeEnum.stream,
      mediaDevices: MediaDevices.getDeviceList(),
      name,
      avatarUrl: activeUserDetails?.avatarUrl,
      termsAccepted: activeUser.getClaim(CLAIM_TERMS_ACCEPTED),
      termsAcceptedAt: activeUser.getClaim(CLAIM_TERMS_ACCEPTED_AT),
      deviceInfo: DeviceService.info
    };
  }

  private async runPreTerminateHooks() {
    try {
      return Promise.all(this.preTerminateHooks.map((hook) => hook.run()));
    } catch (ex) {
      debugError(ex);
    }
  }

  public async terminateSession(reason?: TerminateReasonEnum) {
    try {
      await this.runPreTerminateHooks();

      this._session?.terminate(reason);
    } catch (ex) {
      this.errorHandler.handleError(ex);
    }
    this._session = null;
    this._activeConference = null;

    this.endedObserver?.next(reason);
    this.endedObserver = null;
  }

  public async leaveSession(reason?: TerminateReasonEnum) {
    try {
      await this.runPreTerminateHooks();

      this._session?.leave(reason);
      this._session = null;
      this._activeConference = null;

      this.endedObserver?.next(reason);
      this.endedObserver = null;
    } catch (ex) {
      this.errorHandler.handleError(ex);
    }
  }

  /**
   * Silently re-join the conference after the registration collapsed
   * mid-call and the connection registered again (with a new endpoint id).
   * Armed by the `failed` handler on RTC_TEARDOWN_ERROR, triggered by
   * connectionRegistered$. Clears the stale remote state through the normal
   * removal paths so the store/room tiles clean up, keeps the local media
   * untouched, then joins again — the `accepted` handler re-publishes the
   * local main stream and remote participants re-arrive via endpointJoined /
   * streamAdded.
   */
  private async rejoin() {
    if (!this.rejoinPending) {
      return;
    }
    this.rejoinPending = false;

    if (!this.lastJoinArgs || !this.endedObserver || this._session) {
      return;
    }

    sentryLog(["rejoining conference", this.lastJoinArgs.roomName]);

    // a publish that was in flight when the connection died never settles,
    // leaving the flag stuck and blocking the rejoin's publish
    this.isPublishingStream = false;

    // drop a local screen share; the user restarts it manually
    if (this.local.screenStream?.stream) {
      this.local.closeScreenStream();
    }

    const localIds = new Set(
      [
        this.local.mainStream.stream?.id,
        this.local.secondaryStream?.stream?.id
      ].filter(Boolean)
    );

    // clear remote streams through the existing removal path so the store
    // and any stream-bound features detach. Local streams survive; only drop
    // our bookkeeping (cleanStream would close the live camera/mic because
    // their originator no longer matches the new endpoint id).
    Object.values(this.streams).forEach((stream) => {
      if (localIds.has(stream.id)) {
        delete this.streams[stream.id];
        delete this.streamState[stream.id];
      } else {
        this._streamRemoved.next(stream);
        this.cleanStream(stream);
      }
    });

    // remove remote participants; skip our old self (identified by owning a
    // surviving local stream) so the store keeps the local stream state
    Object.values(this.participants).forEach((participant) => {
      const ownsLocalStream = Object.values(
        this.endpoints[participant.endpoint] ?? {}
      ).some((stream) => localIds.has(stream.id));
      if (!ownsLocalStream) {
        this._endpointLeft.next(participant);
      }
    });

    this.participants = {};
    this.endpoints = {};
    this.endpointState = {};
    this.streamState = {};
    this._participantsChanged.next({});
    this._endpointState.next({});

    try {
      await this.joinConference(
        this.lastJoinArgs.roomName,
        this.lastJoinArgs.origin,
        this.lastJoinArgs.whisperMode
      );
      // the room component's original terminatedObservable subscription is
      // still the active endedObserver (the observable returned above is
      // never subscribed), so the new session reports to the room as before
      this._localEndpointRecovered.next();
    } catch (error) {
      const observer = this.endedObserver;
      this.endedObserver = null;
      observer?.error(error);
    }
  }

  public get sessionExists(): boolean {
    return !!this._session;
  }

  public updateEndpointMetadata(metadata: IEndpointMetadata): Promise<void> {
    if (this.participants[this.myself.endpoint]) {
      const existingMetadata = this.participants[this.myself.endpoint].metadata;
      const nuMetadata = { ...existingMetadata, ...metadata };
      this.participants[this.myself.endpoint].metadata = nuMetadata;
      this._participantsChanged.next(this.participants);

      return this._session.updateMetadata(nuMetadata);
    }
  }

  private getStreamCorrelationId(stream: IStream) {
    return (stream.getMetadata() as IStreamMetadata)?.correlationId;
  }

  public updateStreamMetadata(
    stream: IPublishedStream,
    metadata: IStreamMetadata
  ): void {
    const meta = { ...stream.getMetadata(), ...metadata };
    stream.updateMetadata(meta);
    this._streamMetadataChanged.next({
      userId: this.myself.username,
      userEndpointId: this.myself.endpoint,
      streamId: stream.id,
      newMetadata: meta
    });
  }

  public setConferenceMetadata(data: BaseMetadata): Promise<void> {
    return this._session?.setConferenceMetadata(data.key, data.value);
  }

  public removeConferenceMetadata(
    key: ConferenceMetadataKeyEnum
  ): Promise<void> {
    return this._session?.removeConferenceMetadata(key);
  }

  public getConferenceMetadata(
    key: ConferenceMetadataKeyEnum
  ): IConferenceMetadata {
    return this.conferenceMetadata.get(key);
  }

  public getConferenceView(id: string): Promise<IConferenceView> {
    return this._conferenceResource.get({ urlPostfix: `${id}/simpleView` });
  }

  public getConferenceComplete(id: string): Promise<IConference> {
    return this._conferenceResource.get({ urlPostfix: `${id}` });
  }

  private tryNotifyForMetadata(data: BaseMetadata): boolean {
    // do not deliver to myself or if the data is not an instance of know metadata classes
    if (
      data.userId === this.myself.username ||
      !(data instanceof BaseMetadata)
    ) {
      return;
    }

    // check if the target stream must be available to deliver. an empty endpoint
    // bucket is created on endpointJoined, before any stream arrives, so checking
    // the endpoint alone lets metadata through on join before tiles exist
    const targetExists =
      data instanceof PointerMetadata || data instanceof SnapshotMetadata
        ? !!this.endpoints[data.target.endpoint]?.[data.streamCorrelationId]
        : true;

    const senderExists =
      // for recordings that start with an API call, the api participant that started the recorder leaves the call
      data instanceof RecorderMetadata || data instanceof IntegrationMetadata
        ? true
        : !!this.endpoints[data.userEndpointId];
    const exists = targetExists && senderExists;

    if (exists) {
      debug("notify metadata: exists", data);

      // todo: wait for animation to end to fire this, not use timeout
      setTimeout((_) => {
        this._conferenceMetadataSet.next(data);
      }, 200);
    } else {
      debug("notify metadata: not here", data);
      this.conferenceMetadataQueue.set(data.key, data);
    }

    return exists;
  }

  public checkQueueForPendingMetadata() {
    this.conferenceMetadataQueue.forEach((data, key) => {
      if (this.tryNotifyForMetadata(data)) {
        debug("notify metadata: notified", data);
        this.conferenceMetadataQueue.delete(key);
      }
    });
  }

  public get isConferenceOnHold(): boolean {
    return this._session?.isOnHold();
  }

  public toggleCallHold(): Promise<void> {
    if (!this._session) {
      return;
    }
    if (this._session.isOnHold()) {
      return this._session
        .unhold()
        .then((_) => {
          this.setCallHoldState({ loading: true, isOnHold: false });
          Promise.resolve();
        })
        .catch((err) => {
          this.alertService.error("call-unhold-failed");
          return Promise.reject(err);
        });
    } else {
      return this._session
        .hold()
        .then((_) => {
          this.setCallHoldState({ loading: true, isOnHold: true });
          Promise.resolve();
        })
        .catch((err) => {
          this.alertService.error("call-hold-failed");
          return Promise.reject(err);
        });
    }
  }

  private setCallHoldState(state: ICallHoldState) {
    this.callHoldState = { ...this.callHoldState, ...state };
    this._callHoldState.next(this.callHoldState);
  }

  public getCallHoldState(): ICallHoldState {
    return this.callHoldState;
  }

  public bindElement(element: HTMLAudioElement | HTMLVideoElement, id: string) {
    const stream = this.getStream(id);

    if (!stream) {
      return;
    }

    const existing = Object.entries(this.streamElements).find(
      ([_, elm]) => elm === element
    );

    if (existing && existing[0] !== id) {
      delete this.streamElements[existing[0]];
    }

    this.streamElements[id] = element;
    debug("registered element for stream", element, stream);

    if (stream === this.local.mainStream.stream) {
      this.local.pipe.sink.setSink(element);
    } else {
      element.srcObject = stream.mediaStream;
    }

    requestAnimationFrame(() =>
      MediaDevices.syncSpeaker(
        // @ts-expect-error
        element
      ).catch(debugError)
    );
  }

  public unbindElement(
    element: HTMLAudioElement | HTMLVideoElement,
    id: string
  ) {
    if (element) {
      MediaDevices.desyncSpeaker(
        // @ts-expect-error
        element
      ).catch(() => {});
    }

    debug("unregistered element for stream", id);
  }

  public getElementById(id: string): HTMLVideoElement | HTMLAudioElement {
    return this.streamElements[id];
  }

  /** get any published or not stream */
  public getStream(id: string) {
    return (
      this.streams[id] ||
      (id === this.local.mainStream.stream?.id
        ? this.local.mainStream.stream
        : id === this.local.secondaryStream?.stream?.id
        ? this.local.secondaryStream.stream
        : id === this.local.screenStream.stream?.id
        ? this.local.screenStream.stream
        : null)
    );
  }

  public getPublishedStream(id: string) {
    return this.streams[id];
  }

  /** shows user play button */
  public requestExplicitPlay() {
    this._streamsNeedPlay.next(true);
  }

  public playPausedStreams() {
    this._playPausedStreams.next(null);
  }

  /**
   * Re-attach the audio track of every remote stream element to its srcObject
   * and kick playback. Used to recover audio output on iOS after the audio
   * session is returned to Safari (e.g. a phone call ending). Mirrors the
   * re-attach pattern at the end of `handleStreamAdded`.
   */
  public refreshRemoteAudio() {
    Object.entries(this.streamElements).forEach(([id, element]) => {
      const stream = this.streams[id];
      if (!stream || stream.originator.endpoint === this.myself?.endpoint) {
        return;
      }

      const srcObject = element.srcObject as MediaStream;
      const audioTrack = srcObject?.getAudioTracks()[0];

      if (audioTrack) {
        srcObject.removeTrack(audioTrack);
        srcObject.addTrack(audioTrack);
        element.srcObject = srcObject;
      } else {
        element.srcObject = stream.mediaStream;
      }
    });

    this._playPausedStreams.next(null);
  }

  private republishTimeout = -1;

  private async onLocalStreamError(event: {
    stream: IPublishedStream;
    error: Error;
  }) {
    this.errorHandler.handleError(event.error);

    if (event.stream.type !== StreamTypes.SCREEN) {
      if (this.republishTimeout !== -1) {
        clearTimeout(this.republishTimeout);
      }

      // in case of too many errors
      this.republishTimeout = window.setTimeout(() => {
        this.republishTimeout = -1;
        this.republish();
      }, 4000);
    }
  }

  private async republish() {
    this.unpublishStream(
      this.getStreamCorrelationId(this.local.mainStream?.stream)
    );
    this.publish(this.local.mainStream.stream);
  }

  /**
   * Publish every currently-open local stream. Called from the `accepted`
   * handler so that on a reconnect rejoin the secondary camera (which is not
   * re-opened, only re-published) reaches the remote participant. On an
   * initial join the secondary stream is still null, so this is a main-only
   * publish identical to before. The publishes are awaited in sequence
   * because publish() serializes on `isPublishingStream`.
   */
  private async republishLocalStreams() {
    await this.publish(this.local.mainStream.stream);
    if (this.local.secondaryStream?.stream) {
      await this.publish(this.local.secondaryStream.stream);
    }
  }

  public async publish(stream: IStream) {
    try {
      if (
        !stream ||
        this.isPublishingStream ||
        !this._session?.isEstablished()
      ) {
        return;
      }

      // Nothing to publish (e.g. customer joined with mic off and camera
      // closed in device setup). Publishing a track-less stream throws
      // "Media stream is empty" in @auvious/rtc. Skip it; once a device is
      // enabled, localStreamReady$ re-triggers publish with a non-empty stream.
      if (!stream.mediaStream || stream.mediaStream.getTracks().length === 0) {
        debug("skipping publish of empty stream");
        return;
      }

      this.isPublishingStream = true;
      const myMetadata = this.getMyMetadata();

      const streamToPublish: IPublishOptions = {
        mediaStream: new MediaStream(stream.mediaStream.getTracks()),
        type: stream.type,
        metadata: {
          ...stream.getMetadata(),
          participantName: myMetadata.name,
          participantRoles: myMetadata.roles
        }
      };

      this._localStreamWillPublish.next(streamToPublish);

      await this.zone.runOutsideAngular(() =>
        this._session.publish(streamToPublish)
      );

      if (stream.type === StreamTypes.SCREEN) {
        this.windowEventService.sendMessage(new DeviceCaptureSuccessAction());
      } else {
        this.setFacingModeForStream(
          stream,
          this.local.getFacingModeForStream(stream)
        );
      }
    } catch (ex) {
      debugError(
        `error while trying to get ${stream.type} stream: ${ex.message}`
      );

      if (stream.type === StreamTypes.SCREEN) {
        this.windowEventService.sendMessage(
          new DeviceCaptureErrorAction({ name: ex.name, message: ex.message })
        );
      }

      this.errorHandler.handleError(ex);
      this.alertService.error("Could not publish stream.", { ttl: 3000 });
    } finally {
      this.isPublishingStream = false;
    }
  }

  private unpublishStream(correlationId: string): void {
    const stream = this.endpoints[this.myself.endpoint]?.[correlationId];
    if (stream && this._session?.isEstablished()) {
      this._session.unpublish(stream);
    }
  }

  public unpublishLocalStreams() {
    if (!!this.myself && this.endpoints[this.myself.endpoint]) {
      Object.keys(this.endpoints[this.myself.endpoint]).forEach((key) =>
        this.unpublishStream(key)
      );
    }
  }

  public resolveScreenShareConflict(a: IPublishedStream, b: IPublishedStream) {
    return new Date(a.getMetadata().publishedAt).getTime() >
      new Date(b.getMetadata().publishedAt).getTime()
      ? b
      : a;
  }

  private areStreamsDifferent(a: MediaStream, b: MediaStream) {
    return (
      a !== b ||
      a?.getAudioTracks()[0] !== b?.getAudioTracks()[0] ||
      a?.getVideoTracks()[0] !== b?.getVideoTracks()[0]
    );
  }

  private startRenderReconcile() {
    if (this.renderReconcileTimer) {
      return;
    }
    this.renderReconcileTimer = setInterval(
      () => this.reconcileRenderedStreams(),
      RENDER_RECONCILE_INTERVAL_MS
    );
  }

  private stopRenderReconcile() {
    if (this.renderReconcileTimer) {
      clearInterval(this.renderReconcileTimer);
      this.renderReconcileTimer = null;
    }
    this.unrenderedStrikes = {};
  }

  /**
   * Is this remote stream actually delivering media right now? A remote
   * MediaStreamTrack reports muted=true while no data is arriving, so a live &&
   * !muted track means the PeerConnection is receiving. If every track is muted
   * the far side simply turned everything off — there is nothing to render and
   * nothing to fix.
   */
  private isReceivingMedia(stream: IStream): boolean {
    return !!stream.mediaStream
      ?.getTracks()
      .some((t) => t.readyState === "live" && !t.muted);
  }

  /**
   * Detects and repairs remote streams that are receiving media but were never
   * rendered. See RENDER_RECONCILE_INTERVAL_MS for why this exists.
   */
  private reconcileRenderedStreams() {
    try {
      for (const stream of Object.values(this.streams)) {
        if (!stream || stream.originator?.endpoint === this.myself?.endpoint) {
          continue; // local streams render through a different path
        }

        if (!this.isReceivingMedia(stream)) {
          delete this.unrenderedStrikes[stream.id];
          continue;
        }

        const endpoint = stream.originator.endpoint;
        const correlationId = this.getStreamCorrelationId(stream);
        const inModel = !!this.endpoints[endpoint]?.[correlationId];
        const bound = !!this.streamElements[stream.id];

        if (inModel && bound) {
          delete this.unrenderedStrikes[stream.id];
          continue;
        }

        const strikes = (this.unrenderedStrikes[stream.id] || 0) + 1;
        this.unrenderedStrikes[stream.id] = strikes;
        if (strikes < RENDER_RECONCILE_STRIKES) {
          continue;
        }
        delete this.unrenderedStrikes[stream.id];

        if (!inModel) {
          // The stream is absent from the model that drives the tiles, so no
          // tile — and therefore no media element — was ever created for it.
          // Re-running handleStreamAdded rebuilds the entry, which renders the
          // tile, which binds the element.
          // conferenceId is already on the Sentry scope as a tag (set when the
          // session starts), so it rides along on every captured event.
          captureMessage("remote stream receiving media but not rendered", {
            level: "error",
            extra: {
              streamId: stream.id,
              streamType: stream.type,
              endpoint,
              correlationId,
              endpointState: this.endpointState[endpoint],
              recovered: true
            }
          });

          // handleStreamAdded discards incoming streams whose endpoint is
          // flagged Sick. If that flag is stale — the endpoint is demonstrably
          // delivering media — it would make the repair a no-op, so clear it.
          if (this.endpointState[endpoint] === EndpointStateEnum.Sick) {
            delete this.endpointState[endpoint];
          }

          this.handleStreamAdded(stream);
          this._streamAdded.next(stream);
        } else {
          // In the model but no element bound. This may be legitimate (a tile
          // that is deliberately not rendered), so we only report it. Acting
          // here would risk churning tiles for a failure mode we have never
          // actually observed; if these reports show up in the wild, that is
          // the signal to add remediation.
          captureMessage("remote stream in model but no element bound", {
            level: "warning",
            extra: {
              streamId: stream.id,
              streamType: stream.type,
              endpoint,
              recovered: false
            }
          });
        }
      }
    } catch (ex) {
      this.errorHandler.handleError(ex);
    }
  }

  /** @returns - whether handled */
  private handleStreamAdded(
    stream: IStream | IPublishedStream,
    noMuteHandle?: boolean
  ) {
    debug("stream added", stream);
    if (!stream) {
      return;
    }

    // only one screen stream. Handle it seperately
    if (stream.type === StreamTypes.SCREEN) {
      if (
        this.screenShareStream &&
        this.screenShareStream.originator.endpoint !==
          stream.originator.endpoint
      ) {
        if (
          stream ===
          this.resolveScreenShareConflict(
            this.screenShareStream,
            stream as IPublishedStream
          )
        ) {
          if (this.isScreenShareStreamLocal) {
            this.local.closeScreenStream();
          } else {
            this.cleanStream(this.screenShareStream);
          }
        } else {
          // someone came second, they will close the stream soon
          return false;
        }
      }

      this.screenShareStream = stream as IPublishedStream;
    }

    if (stream.originator.endpoint === this.myself.endpoint) {
      this.local.replaceStream(stream as IPublishedStream);
    } else if (
      this.streamElements[stream.id] &&
      this.areStreamsDifferent(
        this.streamElements[stream.id].srcObject as MediaStream,
        stream.mediaStream
      )
    ) {
      const srcObject = this.streamElements[stream.id].srcObject as MediaStream;
      let oldTrack = srcObject?.getVideoTracks()[0];
      let newTrack = stream.mediaStream?.getVideoTracks()[0];

      if (oldTrack !== newTrack) {
        // video will flicker anyway
        this.streamElements[stream.id].srcObject = stream.mediaStream;
      } else {
        oldTrack = srcObject?.getAudioTracks()[0];
        newTrack = stream.mediaStream?.getAudioTracks()[0];

        if (oldTrack !== newTrack) {
          if (oldTrack) srcObject?.removeTrack(oldTrack);
          if (newTrack) {
            srcObject?.addTrack(newTrack);
            // June 2025, after unmute, the remote stream cannot be heard
            this.streamElements[stream.id].srcObject = srcObject;
          }
        }
      }
    }

    this.endpoints[stream.originator.endpoint] ??= {};

    // if incoming stream is already sick (leftover from previous tab), discard it
    if (
      this.endpointState[stream.originator.endpoint] === EndpointStateEnum.Sick
    ) {
      return false;
    }

    this.endpoints[stream.originator.endpoint] = {
      ...this.endpoints[stream.originator.endpoint],
      [this.getStreamCorrelationId(stream)]: stream
    };

    if (stream.type === StreamTypes.SCREEN && !this.isScreenShareStreamLocal) {
      // TODO isWidget || isPopup
      this.windowEventService.sendMessage(
        new ScreenShareAvailabilityChangedAction(true)
      );
    }

    // TODO: why?
    // if (!noMuteHandle && this.isMutedWhileSwitchingCam) {
    //   setTimeout(() => {
    //     this.mute(StreamTrackKindEnum.audio).then(() => {
    //       this.isMutedWhileSwitchingCam = undefined;
    //       this._endpointState.next({ ...this.endpointState });
    //     });
    //   }, 300);
    // }

    return true;
  }

  public closeLocalStreams() {
    this.local.closeMainStream();

    if (this.myself && this.endpoints[this.myself.endpoint]) {
      Object.values(this.endpoints[this.myself.endpoint]).forEach((stream) => {
        this.cleanStream(stream);
      });
    }

    if (this.local.screenStream) {
      this.local.closeScreenStream();
    }
  }

  public get localMediaStream(): MediaStream | null {
    return this.localStream?.mediaStream || null;
  }

  public get localStream(): IPublishedStream | null {
    return this.local.mainStream.stream;
  }

  public get isScreenShareStreamLocal() {
    return this.screenShareStream?.originator.endpoint === this.myself.endpoint;
  }

  public isMutedStream(
    stream: IStream | IPublishedStream,
    kind: StreamTrackKindEnum
  ): boolean {
    if (!stream) {
      return false;
    } else if (stream.isMuted) {
      return stream.isMuted(kind);
    } else if (
      stream.id in this.mutedStreams &&
      kind in this.mutedStreams[stream.id]
    ) {
      return this.mutedStreams[stream.id][kind];
    } else if (stream.type !== StreamTypes.VIDEO) {
      return (
        (kind === StreamTrackKindEnum.video &&
          stream.type === StreamTypes.MIC) ||
        (kind === StreamTrackKindEnum.audio && stream.type === StreamTypes.CAM)
      );
    } else {
      return false;
    }
  }

  public isMutedStreamId(id: string, kind: StreamTrackKindEnum): boolean {
    const stream = this.streams[id];

    if (!stream) {
      return false;
    } else if (stream.isMuted) {
      return stream.isMuted(kind);
    } else if (
      stream.id in this.mutedStreams &&
      kind in this.mutedStreams[stream.id]
    ) {
      return this.mutedStreams[stream.id][kind];
    } else if (stream.type !== StreamTypes.VIDEO) {
      return (
        (kind === StreamTrackKindEnum.video &&
          stream.type === StreamTypes.MIC) ||
        (kind === StreamTrackKindEnum.audio && stream.type === StreamTypes.CAM)
      );
    } else {
      return false;
    }
  }

  public terminateConferenceRequest(
    reason: TerminateReasonEnum = TerminateReasonEnum.terminate
  ) {
    this._terminateConference.next(reason);
  }

  public confirmTerminateConferenceRequest() {
    this._confirmTerminateConference.next();
  }

  private reset() {
    this.stopRenderReconcile();
    this.streamElements = {};
    this.endpoints = {};
    this.endpointState = {};
    this.streamState = {};
    this.participants = {};
    this.mutedStreams = {};
    this.streamWarningsTimeouts = {};
    this.callHoldState = { ...DEFAULT_CALL_HOLD_STATE };
    this.preTerminateHooks = [];
  }

  public destroy() {
    this.local.freezeStreamType = false;

    this.reset();
    this.conferenceMetadata.clear();
    this.conferenceMetadataQueue.clear();
    this._endpointMetadataChanged.next(null);
    this._conferenceMetadataSet.next(null);
    this._localStreamWillPublish.next(null);
    this._localStreamPublished.next(null);
    this._endpointStateChanged.next({
      endpoint: this.myself.endpoint,
      state: EndpointStateEnum.Left
    });
  }

  public getParticipants() {
    return this.participants;
  }

  public getParticipantAgents(): IEndpoint<IEndpointMetadata>[] {
    return Object.values(this.participants).filter((p) =>
      (p.metadata as IEndpointMetadata)?.roles.includes(UserRoleEnum.agent)
    );
  }

  public getStreamsForEndpoint(endpoint: string): {
    [correlationId: string]: IConferenceStream;
  } {
    return this.endpoints[endpoint];
  }

  private findFacingMode(stream: IStream) {
    if (StreamTypes.CAM === stream.type || StreamTypes.VIDEO === stream.type) {
      const metadata = this.local.getStreamMetadata(stream.mediaStream);

      // iOS 14.x has facingMode in settings
      // chrome 92.x has facingMode in constraints
      // firefox 91.x has facingMode in settings
      // safari 14.x has NO facingMode
      return metadata.video?.constraints?.facingMode ||
        metadata.video?.settings?.facingMode ||
        (stream.originator.endpoint === this.myself.endpoint &&
          this.local.pipe.streamOut === stream.mediaStream)
        ? this.local.pipe.input.facingMode
        : VideoFacingModeEnum.User;
    } else {
      return VideoFacingModeEnum.Environment;
    }
  }

  public getFacingModeForStream(stream: IStream) {
    return this.streamFacingMode.get(stream);
  }

  public setFacingModeForStream(
    stream: IStream,
    facingMode: VideoFacingModeEnum
  ) {
    this.streamFacingMode.set(stream, facingMode);
    this._facingModeChanged.next({ streamId: stream.id, facingMode });
  }

  public get myself(): IEndpoint {
    return this.rtcService.myself;
  }
}
